/**
 * Who can read the current space (#1069 POC).
 *
 * READ-ONLY BY DESIGN. Membership is automatic: everyone on the request team
 * belongs to every thread in the spaces they are entitled to see. There is no
 * per-thread invitation, so there is nothing to add or remove here. People join
 * a conversation by being on the request, and they leave it the same way.
 *
 * ⚠️ THIS FILE NO LONGER STATES THE AUDIENCE, AND THAT IS THE POINT (#1069
 * Story 5.2). It used to open with a tinted band reading "Internal. The
 * customer cannot see this." — built as deliberate redundancy back when the
 * only other signal was a 3px border and a tab that looked like every other
 * tab. By the time the Vertiv tab carried a lock icon and a permanent hint,
 * and the composer named the audience at the moment of writing, the redundancy
 * had become literal duplication: the Shared tab said "The customer can read
 * this" and the band directly beneath it said "Shared. The customer can read
 * this." The owner removed it on 2026-09-08.
 *
 * What carries the audience now, all of it outside this file: the tab's icon,
 * the tab's label, the tab's hint (`CorrespondencePanel.tsx:813`, `:827`), and
 * the composer's notice (`Composer.tsx:871`). Three of those four are
 * non-colour, so NFR29 still holds with margin. (The panel's coloured top
 * border was a fifth until the owner removed it on 2026-09-08.)
 *
 * This strip's remaining job is the membership row: how many people can read
 * the space, and who they are on demand.
 */
import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';

import { colors } from '../../theme/colors';
import { Users } from '../icons';

import type { SpaceId } from './correspondence.types';
import type { SpaceMember } from './team';

interface Props {
  members: SpaceMember[];
  space: SpaceId;
  /**
   * Where the panel's team read stands (Story 5.4 C11, widened at the
   * 2026-10-06 review). `'loading'` while the team is being read, `'unavailable'`
   * after a load error or a revoke, `'ready'` once the rows are in hand. While
   * it is anything but `'ready'` the list is only the requestor fold, so a
   * count, or "No team members", would be the list guessing out loud. The
   * review found the earlier boolean said "Loading" forever on an error, above
   * the very bar that said the load had failed.
   */
  teamStatus: 'loading' | 'unavailable' | 'ready';
}

export const TeamStrip: React.FC<Props> = ({ members, space, teamStatus }) => {
  const [expanded, setExpanded] = useState(false);
  const internal = space === 'internal';
  const externals = members.filter(m => !m.isInternal).length;
  const where = internal ? 'the internal space' : 'this';
  // Since the viewer is no longer folded in (2026-10-06) the list can be
  // empty: an internal viewer with no team row on a team of externals. Say so
  // rather than "0 people", and keep the toggle so the note still explains.
  // ONE string for the eye and the screen reader: the a11y label used to be a
  // second ternary that said "1 people" where this said "1 person".
  const summary =
    teamStatus === 'loading'
      ? `Loading who can read ${where}`
      : teamStatus === 'unavailable'
      ? `Who can read ${where} is unavailable`
      : members.length === 0
      ? `No team members can read ${where}`
      : `${members.length} ${
          members.length === 1 ? 'person' : 'people'
        } can read ${where}${
          !internal && externals > 0
            ? `, including ${externals} outside Vertiv`
            : ''
        }`;

  return (
    <View style={styles.wrap}>
      <TouchableOpacity
        style={styles.summary}
        onPress={() => setExpanded(v => !v)}
        testID="correspondence-team-toggle"
        accessibilityLabel={summary}
      >
        <Users size={13} color={colors.textMuted} />
        <Text style={styles.summaryText} numberOfLines={1}>
          {summary}
        </Text>
        <Text style={styles.toggle}>{expanded ? 'Hide' : 'Show'}</Text>
      </TouchableOpacity>

      {expanded ? (
        <View style={styles.body}>
          {members.map(m => (
            <View key={m.id} style={styles.row} testID={`team-member-${m.id}`}>
              <View style={styles.rowMain}>
                <Text style={styles.name} numberOfLines={1}>
                  {m.name}
                </Text>
                {/* A non-member author has no address on the wire; no line. */}
                {m.email ? (
                  <Text style={styles.email} numberOfLines={1}>
                    {m.email}
                  </Text>
                ) : null}
              </View>
              <Text style={[styles.role, !m.isInternal && styles.roleExternal]}>
                {m.roleLabel}
              </Text>
            </View>
          ))}
          <Text style={styles.note}>
            Membership follows the request team. Change it in Team Information.
          </Text>
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    borderBottomWidth: 1,
    borderBottomColor: colors.borderPanel,
    backgroundColor: colors.surfacePanelMuted,
  },
  summary: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 8,
  },
  summaryText: { flex: 1, fontSize: 12, color: colors.textSecondary },
  /**
   * BLUE, not brand orange (#1069 Story 5.1). Show / Hide is a link, and at the
   * time the panel spent its orange on audience (the internal top border and
   * the selected tab's underline, both since removed on 2026-09-08). One accent
   * could not mean both "this is the internal space" and "this is pressable"
   * without one of them going unread. By the end of that day the owner had
   * taken orange off the icons and the composer too; the only `primary` left
   * in the panel is the loading spinners and the dock's resize-handle highlight.
   */
  toggle: { fontSize: 11, fontWeight: '700', color: colors.textLink },

  body: { paddingHorizontal: 14, paddingBottom: 10, gap: 7 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  rowMain: { flex: 1 },
  name: { fontSize: 13, fontWeight: '600', color: colors.textPrimary },
  email: { fontSize: 10, color: colors.textMuted },
  role: {
    fontSize: 9,
    fontWeight: '700',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: colors.textSecondary,
  },
  roleExternal: { color: colors.textOnWarningMuted },
  note: {
    marginTop: 4,
    fontSize: 10,
    fontStyle: 'italic',
    color: colors.textMuted,
  },
});
